// Stimulus controller for the time entry edit form.
//
// Wires the Customer / Project / Task selects so changing customer reloads the
// project options, and changing project reloads the task options. The
// time_entries#projects and time_entries#tasks endpoints already exist and
// return HTML <option> fragments suitable for innerHTML.
//
// Those legacy endpoints prepend an "All ..." placeholder option (value="A")
// intended for the filter form. In the edit context "All" isn't a valid pick,
// so after each cascade we drop the placeholder and select the first real
// option (auto-cascading into the next level).
//
// Also gates the Save button: TimeEntry requires a task, so if the task select
// is empty (no tasks for the chosen project) the form would be rejected by the
// model. Disable Save when that happens.
import { Controller } from "@hotwired/stimulus"
import { parseTimeOfDay } from "../lib/time_of_day"

export default class extends Controller<HTMLElement> {
  static targets = ["customer", "project", "task", "submit", "startedTime", "endedTime"]
  declare readonly customerTarget: HTMLSelectElement
  declare readonly projectTarget: HTMLSelectElement
  declare readonly taskTarget: HTMLSelectElement
  declare readonly submitTarget: HTMLInputElement | HTMLButtonElement
  declare readonly startedTimeTarget: HTMLInputElement
  declare readonly endedTimeTarget: HTMLInputElement
  declare readonly hasStartedTimeTarget: boolean
  declare readonly hasEndedTimeTarget: boolean

  connect(): void {
    this.updateValidity()
  }

  timeChanged(): void {
    this.updateValidity()
  }

  customerChanged(): void {
    const customerId = this.customerTarget.value
    if (!customerId || customerId === "A") return
    fetch(`/time_entries/projects?customer_id=${encodeURIComponent(customerId)}`, {
      headers: { "X-Requested-With": "XMLHttpRequest", Accept: "text/html" },
    })
      .then(r => r.text())
      .then(html => {
        this.projectTarget.innerHTML = html
        this.dropPlaceholder(this.projectTarget)
        this.projectChanged()
      })
  }

  projectChanged(): void {
    const projectId = this.projectTarget.value
    if (!projectId || projectId === "A") {
      this.taskTarget.innerHTML = ""
      this.updateValidity()
      return
    }
    fetch(`/time_entries/tasks?project_id=${encodeURIComponent(projectId)}`, {
      headers: { "X-Requested-With": "XMLHttpRequest", Accept: "text/html" },
    })
      .then(r => r.text())
      .then(html => {
        this.taskTarget.innerHTML = html
        this.dropPlaceholder(this.taskTarget)
        this.updateValidity()
      })
  }

  taskChanged(): void {
    this.updateValidity()
  }

  // Form is valid when a real task is selected and any provided start/end
  // times parse as valid times of day. TimeEntry has a validates_presence_of
  // :task — and the cascading list can leave it empty — so we mirror it here.
  private updateValidity(): void {
    const taskId = this.taskTarget.value
    let valid = !!taskId && taskId !== "A"
    let reason = ""
    if (!valid) reason = "Pick a task to save"

    if (this.hasStartedTimeTarget) {
      const s = this.startedTimeTarget.value
      const ok = s.trim() === "" || parseTimeOfDay(s).valid
      this.startedTimeTarget.classList.toggle("!border-[var(--color-state-red-500)]", !ok)
      if (!ok) { valid = false; reason = "Start time isn't a valid time" }
    }
    if (this.hasEndedTimeTarget) {
      const e = this.endedTimeTarget.value
      const ok = e.trim() === "" || parseTimeOfDay(e).valid
      this.endedTimeTarget.classList.toggle("!border-[var(--color-state-red-500)]", !ok)
      if (!ok) { valid = false; reason = "End time isn't a valid time" }
    }

    this.submitTarget.disabled = !valid
    this.submitTarget.classList.toggle("opacity-40", !valid)
    this.submitTarget.classList.toggle("cursor-not-allowed", !valid)
    this.submitTarget.title = reason
  }

  // Remove the legacy "All …" placeholder (value="A") if present, so a real
  // option lands at index 0 and is auto-selected.
  private dropPlaceholder(select: HTMLSelectElement): void {
    const first = select.options[0]
    if (first && first.value === "A") select.remove(0)
    if (select.options.length > 0) select.selectedIndex = 0
  }
}
